/**
 * Prefetch Utility
 * 
 * This utility helps optimize the user experience by prefetching
 * components that are likely to be needed soon.
 */

/**
 * Prefetches a component by importing it but not rendering it
 * @param importFn - The import function for the component
 */
export const prefetchComponent = (importFn: () => Promise<any>) => {
  // Start the import but don't wait for it
  importFn().catch(err => {
    console.warn('Error prefetching component:', err);
  });
};

/**
 * Prefetches multiple components
 * @param importFns - Array of import functions for components
 */
export const prefetchComponents = (importFns: Array<() => Promise<any>>) => {
  importFns.forEach(prefetchComponent);
};

/**
 * Sets up prefetching based on scroll position
 * @param threshold - Percentage of page scrolled before prefetching (0-1)
 * @param importFns - Array of import functions for components to prefetch
 */
export const setupScrollPrefetch = (
  threshold: number = 0.2,
  importFns: Array<() => Promise<any>>
) => {
  let hasPrefetched = false;
  
  const handleScroll = () => {
    if (hasPrefetched) return;
    
    // Calculate how far down the page the user has scrolled
    const scrollTop = window.scrollY || document.documentElement.scrollTop;
    const windowHeight = window.innerHeight;
    const documentHeight = Math.max(
      document.body.scrollHeight,
      document.body.offsetHeight,
      document.documentElement.clientHeight,
      document.documentElement.scrollHeight,
      document.documentElement.offsetHeight
    );
    
    // If user has scrolled past the threshold, prefetch components
    if (scrollTop / (documentHeight - windowHeight) > threshold) {
      prefetchComponents(importFns);
      hasPrefetched = true;
      
      // Remove the scroll listener once prefetching is done
      window.removeEventListener('scroll', handleScroll);
    }
  };
  
  // Add scroll listener
  window.addEventListener('scroll', handleScroll, { passive: true });
  
  // Return a function to clean up the listener
  return () => {
    window.removeEventListener('scroll', handleScroll);
  };
};
